/**
 * Página de Inicio Inmersiva
 * Diseño dinámico tipo Heinz/Awwwards con scrollytelling
 * Contenido según guía oficial de estructura
 * 
 * Secciones:
 * 1. Hero principal a pantalla completa
 * 2. Bloque "Compromiso con lo que te gusta"
 * 3. Categorías de productos (Retail, Horeca)
 * 4. Recetas destacadas
 * 5. Cierre emocional "Somos Intercasa. Somos Canarias."
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../components/common/LocalizedLink';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import {
  AnimatedSection,
  AnimatedText,
  ParallaxImage,
  MagneticButton,
  HeroSection,
} from '../components/animations';
import { getRecipeById } from '../data/recipesData';
import useLocalizeContent from '../hooks/useLocalizeContent';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de inicio con diseño inmersivo
 * Estructura según guía oficial de Intercasa
 * @returns {JSX.Element} Página de inicio
 */
const HomePage = () => {
  const { t } = useTranslation('home');
  const { localize } = useLocalizeContent();
  const compromiseRef = useRef(null);
  const ctaRef = useRef(null);

  // Categorías de productos según guía
  const productCategories = [
    {
      id: 'retail',
      name: t('categories.retail.name'),
      description: t('categories.retail.description'),
      link: '/productos/retail',
      image: '/images/inicio/productos-retail.webp',
    },
    {
      id: 'horeca',
      name: t('categories.horeca.name'),
      description: t('categories.horeca.description'),
      link: '/productos/horeca',
      image: '/images/inicio/productos-horeca.webp',
    },
  ];

  // Recetas destacadas (3 según guía) - IDs: 1 (Pollo glaseado), 4 (Costillas BBQ), 10 (Papas bravas)
  const featuredRecipes = localize([
    getRecipeById(1),  // Pollo glaseado con ketchup y miel
    getRecipeById(4),  // Costillas BBQ al horno
    getRecipeById(10), // Papas bravas canarias con alioli
  ].filter(Boolean));

  // Animaciones de scroll personalizadas
  useEffect(() => {
    const compromiseSection = compromiseRef.current;
    if (compromiseSection) {
      const textElements = compromiseSection.querySelectorAll('.story-text');
      
      textElements.forEach((text, index) => {
        gsap.fromTo(text,
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            delay: index * 0.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: text,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO PRINCIPAL - Pantalla completa
        Título: "Sabor de tradición canaria."
        Subtítulo: "Salsas y productos elaborados en Canarias..."
        CTA: Ver productos
        =============================================
      */}
      <HeroSection
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        backgroundVideo="/videos/tomates-optimized.mp4"
        height={100}
        textPosition="center"
        overlayGradient="linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)"
      >
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
          <LocalizedLink to="/productos">
            <MagneticButton variant="primary" size="lg">
              {t('hero.cta')}
            </MagneticButton>
          </LocalizedLink>
        </div>
      </HeroSection>

      {/* 
        =============================================
        BLOQUE "NUESTROS IMPRESCINDIBLES"
        3 productos destacados con imagen y descripción
        Paleta: Fondo Rojo Intercasa (#C1052F), Cards blancas
        =============================================
      */}
      <section className="relative bg-intercasa-red py-24 md:py-32 overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-intercasa-yellow/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          {/* Título de sección */}
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <h2 className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl mb-4">
              {t('classics.title')}
            </h2>
            <p className="text-center font-lato text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              {t('classics.subtitle')}
            </p>
          </AnimatedSection>

          {/* Grid de productos imprescindibles - Fila superior: Ketchup y Barbacoa */}
          <div className="grid md:grid-cols-6 gap-8 max-w-6xl mx-auto mb-8">
            {/* Ketchup - El de toda la vida */}
            <AnimatedSection animation="fadeUp" delay={0.1} className="group md:col-span-2 md:col-start-2">
              <LocalizedLink to="/productos/retail/ketchup-cristal" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/ketchup.webp" 
                      alt={t('classics.ketchup.alt')}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      {t('classics.ketchup.tagline')}
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      {t('classics.ketchup.description')}
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>{t('classics.viewProduct')}</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </LocalizedLink>
            </AnimatedSection>

            {/* Barbacoa Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.2} className="group md:col-span-2">
              <LocalizedLink to="/productos/retail/salsa-barbacoa-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/barbacoa.webp" 
                      alt="Barbacoa Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Barbacoa Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      {t('classics.barbecue.description')}
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>{t('classics.viewProduct')}</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </LocalizedLink>
            </AnimatedSection>
          </div>

          {/* Grid de productos imprescindibles - Fila inferior: Mayonesa, Mostaza y Alioli */}
          <div className="grid md:grid-cols-6 gap-8 max-w-6xl mx-auto">
            {/* Mayonesa Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.3} className="group md:col-span-2">
              <LocalizedLink to="/productos/retail/mayonesa-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/mayonesa.webp" 
                      alt="Mayonesa Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Mayonesa Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      {t('classics.mayonnaise.description')}
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>{t('classics.viewProduct')}</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </LocalizedLink>
            </AnimatedSection>

            {/* Mostaza Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.4} className="group md:col-span-2">
              <LocalizedLink to="/productos/retail/mostaza-dulce-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/mostaza.webp" 
                      alt="Mostaza Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Mostaza Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      {t('classics.mustard.description')}
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>{t('classics.viewProduct')}</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </LocalizedLink>
            </AnimatedSection>

            {/* Alioli Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.5} className="group md:col-span-2">
              <LocalizedLink to="/productos/retail/alioli-receta-canaria-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/alioli.webp" 
                      alt="Alioli Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Alioli Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      {t('classics.alioli.description')}
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>{t('classics.viewProduct')}</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </LocalizedLink>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. BLOQUE "COMPROMISO CON LO QUE TE GUSTA"
        Diseño: 2 columnas (Imagen + Texto)
        =============================================
      */}
      <section ref={compromiseRef} className="relative bg-white overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-screen">
          {/* Columna de imagen - Fábrica, ingredientes o proceso */}
          <div className="relative h-[50vh] lg:h-auto overflow-hidden order-2 lg:order-1">
            <ParallaxImage
              src="/images/inicio/quienes-somos.webp"
              alt={t('commitment.imageAlt')}
              speed={0.3}
              scale={false}
              className="absolute inset-0 h-full"
              imgClassName="scale-[0.55]"
              overlay={true}
              overlayOpacity={0.1}
            />
          </div>

          {/* Columna de texto */}
          <div className="flex items-center justify-center p-8 md:p-16 lg:p-24 order-1 lg:order-2 bg-intercasa-bone">
            <div className="max-w-xl">
              <AnimatedText
                text={t('commitment.title')}
                as="h2"
                animation="words"
                className="font-baskerville font-bold text-intercasa-dark text-4xl md:text-5xl lg:text-6xl mb-8"
              />
              
              <div className="space-y-6 story-text">
                <p className="font-lato text-lg md:text-xl text-intercasa-dark/80 leading-relaxed">
                  {t('commitment.text1')}
                </p>
                <p className="font-lato text-lg md:text-xl text-intercasa-dark/80 leading-relaxed">
                  {t('commitment.text2')}
                </p>
              </div>

              <AnimatedSection animation="fadeUp" delay={0.3} className="mt-10">
                <LocalizedLink to="/nosotros">
                  <MagneticButton variant="primary" size="lg">
                    {t('commitment.cta')}
                  </MagneticButton>
                </LocalizedLink>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. BLOQUE DE CATEGORÍAS DE PRODUCTOS
        Grid de categorías con imagen, nombre y botón
        =============================================
      */}
      <section className="relative bg-intercasa-red py-24 md:py-32 overflow-hidden">
        {/* Fondo decorativo */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-intercasa-yellow rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          {/* Título de sección */}
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <h2 className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl mb-4">
              {t('range.title')}
            </h2>
            <p className="text-center font-lato text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              {t('range.subtitle')}
            </p>
          </AnimatedSection>

          {/* Grid de categorías - 2 columnas según guía */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {productCategories.map((category, index) => (
              <AnimatedSection
                key={category.id}
                animation="scale"
                delay={index * 0.15}
                className="group"
              >
                <LocalizedLink 
                  to={category.link}
                  className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 relative opacity-100 hover:opacity-100"
                >
                  {/* Imagen de categoría */}
                  <div className="aspect-[16/10] bg-intercasa-bone overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  
                  {/* Contenido */}
                  <div className="p-8">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl mb-2 group-hover:text-intercasa-red transition-colors">
                      {category.name}
                    </h3>
                    <p className="font-lato text-intercasa-dark/60 text-lg mb-6">
                      {category.description}
                    </p>
                    <span className="inline-flex items-center font-lato font-semibold text-intercasa-red group-hover:gap-3 gap-2 transition-all">
                      {t('range.viewCategory')}
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </div>

                  {/* Barra decorativa amarilla en hover */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </LocalizedLink>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 
        =============================================
        4. BLOQUE RECETAS DESTACADAS
        Tres recetas en cards (Imagen, Título, Enlace)
        =============================================
      */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
              {t('recipes.label')}
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-4xl md:text-5xl lg:text-6xl mb-4">
              {t('recipes.title')}
            </h2>
            <p className="text-center font-lato text-intercasa-dark/60 text-lg max-w-2xl mx-auto">
              {t('recipes.subtitle')}
            </p>
          </AnimatedSection>

          {/* Grid de recetas - 3 cards según guía */}
          <div className="grid md:grid-cols-3 gap-8">
            {featuredRecipes.map((recipe, index) => (
              <AnimatedSection
                key={recipe.id}
                animation="fadeUp"
                delay={index * 0.15}
              >
                <LocalizedLink 
                  to={recipe.url}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-2xl bg-intercasa-bone shadow-lg hover:shadow-2xl transition-shadow duration-500">
                    {/* Imagen */}
                    <div className="aspect-[4/3] overflow-hidden">
                      {recipe.imageUrl ? (
                        <img 
                          src={recipe.imageUrl} 
                          alt={recipe.imageAlt || recipe.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-intercasa-bone flex items-center justify-center group-hover:scale-110 transition-transform duration-700">
                          <svg className="w-20 h-20 text-intercasa-red opacity-30" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Contenido */}
                    <div className="p-6 bg-white h-56">
                      <span className="font-lato text-intercasa-red text-xs uppercase tracking-wider">
                        {recipe.category}
                      </span>
                      <h3 className="font-baskerville font-semibold text-xl md:text-2xl text-intercasa-dark mt-2 group-hover:text-intercasa-red transition-colors">
                        {recipe.title}
                      </h3>
                      <p className="font-lato text-intercasa-dark/60 mt-2 text-left text-base leading-relaxed">
                        {recipe.description}
                      </p>
                    </div>

                    {/* Línea decorativa */}
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                  </div>
                </LocalizedLink>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection animation="fadeUp" delay={0.4} className="text-center mt-12">
            <LocalizedLink to="/recetas">
              <MagneticButton variant="primary" size="lg">
                {t('recipes.viewAll')}
              </MagneticButton>
            </LocalizedLink>
          </AnimatedSection>
        </div>
      </section>

      {/* 
        =============================================
        5. BLOQUE DE CIERRE EMOCIONAL
        Título: "Somos Intercasa. Somos Canarias."
        CTA: "Conócenos mejor"
        =============================================
      */}
      <section 
        ref={ctaRef}
        className="relative py-24 md:py-32 overflow-hidden"
      >
        {/* Imagen de fondo */}
        <div className="absolute inset-0">
          <img 
            src="/images/inicio/somos-intercasa.webp" 
            alt={t('closing.imageAlt')} 
            className="w-full h-full object-cover"
          />
          {/* Overlay rojo */}
          <div className="absolute inset-0 bg-intercasa-red/85"></div>
        </div>

        {/* Elementos decorativos */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-intercasa-yellow/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>

        {/* Contenido */}
        <div className="relative z-10 w-full">
          <div className="container mx-auto px-4 md:px-8 text-center max-w-4xl">
            <AnimatedText
              text={t('closing.title')}
              as="h2"
              animation="words"
              className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-7xl mb-8"
            />
            
            <AnimatedSection animation="fadeUp" delay={0.3}>
              <p className="text-center font-lato text-xl md:text-2xl text-white/90 mb-6 leading-relaxed">
                {t('closing.text')}
              </p>
            </AnimatedSection>

            {/* Estadísticas - Datos reales según guía (fundación 1949) */}
            <AnimatedSection animation="fadeUp" delay={0.4}>
              <div className="grid grid-cols-3 gap-8 my-12 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="font-baskerville font-bold text-intercasa-yellow text-4xl md:text-5xl">
                    1949
                  </div>
                  <div className="font-lato text-white/70 text-sm uppercase tracking-wider mt-1">
                    {t('closing.stats.founded')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-baskerville font-bold text-intercasa-yellow text-4xl md:text-5xl">
                    100%
                  </div>
                  <div className="font-lato text-white/70 text-sm uppercase tracking-wider mt-1">
                    {t('closing.stats.canarian')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-baskerville font-bold text-intercasa-yellow text-4xl md:text-5xl">
                    75+
                  </div>
                  <div className="font-lato text-white/70 text-sm uppercase tracking-wider mt-1">
                    {t('closing.stats.years')}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.5}>
              <LocalizedLink to="/nosotros">
                <MagneticButton variant="white" size="xl">
                  {t('closing.cta')}
                </MagneticButton>
              </LocalizedLink>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/*
        =============================================
        6. CARRUSEL DE SUBVENCIONES
        Muestra las subvenciones recibidas
        =============================================
      */}
      <SubsidiesGrid />

    </ImmersiveLayout>
  );
};

/**
 * Datos de las subvenciones
 */
const subsidiesData = [
  {
    image: '/images/subvenciones/Subvencion3.webp',
    altKey: 'subsidies.items.maritime',
    pdf: '/images/subvenciones/PDF/SubvencionesMaritimo.pdf',
    odt: '/images/subvenciones/PDF/SubvencionesMaritimo.docx',
  },
  {
    image: '/images/subvenciones/Cartel Intercas Autoconsumo.jpg',
    altKey: 'subsidies.items.selfConsumption',
    pdf: '/images/subvenciones/PDF/Cartel Intercas Autoconsumo.pdf',
    odt: '/images/subvenciones/PDF/Autoconsumo.odt',
  },
  {
    image: '/images/subvenciones/Cartel Intercasa Prórroga.jpg',
    altKey: 'subsidies.items.extension',
    pdf: '/images/subvenciones/PDF/Cartel Intercasa Prórroga.pdf',
    odt: '/images/subvenciones/PDF/Prorroga.docx',
  },
];

/**
 * Componente de cuadrícula de subvenciones - muestra todos los carteles a la vez
 */
const SubsidiesGrid = () => {
  const { t } = useTranslation('home');

  return (
    <section className="relative py-20 md:py-28 bg-intercasa-bone/30 overflow-hidden">
      {/* Decoración */}
      <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-intercasa-red/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-intercasa-yellow/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 px-4 md:px-6">
        {/* Encabezado */}
        <AnimatedSection animation="fadeUp">
          <div className="text-center mb-12">
            <span className="inline-block font-lato text-intercasa-red text-sm uppercase tracking-[0.2em] font-medium mb-4">
              {t('common:nav.transparency')}
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl mb-4">
              {t('subsidies.title')}
            </h2>
            <p className="font-lato text-intercasa-dark/60 text-lg max-w-2xl mx-auto">
              {t('subsidies.subtitle')}
            </p>
          </div>
        </AnimatedSection>

        {/* Carteles de subvenciones */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto">
          {subsidiesData.map((sub, i) => (
            <AnimatedSection key={i} animation="fadeUp" delay={0.1 + i * 0.15}>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-intercasa-bone hover:shadow-xl transition-shadow duration-300">
                {/* Imagen del cartel */}
                <div className="bg-white p-6 md:p-8 flex items-center justify-center">
                  <img
                    src={sub.image}
                    alt={t(sub.altKey)}
                    className="w-full h-auto object-contain rounded-lg"
                  />
                </div>

                {/* Botones de descarga */}
                <div className="flex justify-center gap-4 p-5 border-t border-intercasa-bone/50">
                  <a
                    href={sub.pdf}
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium text-sm"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('subsidies.downloadPdf')}
                  </a>
                  <a
                    href={sub.odt}
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium text-sm"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('subsidies.downloadOdt')}
                  </a>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Enlace a página completa */}
        <AnimatedSection animation="fadeUp" delay={0.6}>
          <div className="text-center mt-10">
            <LocalizedLink to="/transparencia/ayudas">
              <MagneticButton variant="primary" size="lg">
                {t('subsidies.viewAll')}
              </MagneticButton>
            </LocalizedLink>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default HomePage;